import { forwardRef, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Highlighter, Typeahead } from 'react-bootstrap-typeahead';
import { EllipsisText, WrappedText } from './DropdownButton';
import CheckIcon from '../icons/CheckIcon';

export const sidePanelTypeaheadStyles = css`
  .rbt-input {
    font-size: 1em;
    height: 1.5rem;
    padding: 1px 5px;
    background-color: var(--side-panel-color);
    border: 1px solid var(--side-panel-color);
    color: var(--text-color);
    text-overflow: ellipsis;
    &::placeholder {
      color: var(--text-color);
    }
    &:hover,
    &:focus {
      border-color: var(--border-color);
      background-color: #fff;
      box-shadow: none;
    }
    &:focus::placeholder {
      color: rgba(33, 33, 33, 0.5);
    }
  }
  .rbt-menu {
    max-width: calc(var(--side-panel-width) - 2rem);
    overflow: hidden;
  }
  .rbt-close {
    padding-top: 5px;
  }
`;

export const standardTypeaheadStyles = css`
  .dropdown-menu {
    font-size: 1em;
    // N.b. There is a built-in maxHeight prop on the Typeahead component
    ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth};`}
    ${({ minWidth }) => minWidth && `min-width: ${minWidth};`}
    .dropdown-header, .dropdown-item {
      // N.b. If paddingX is less than 14px, showCheckmark will not render nicely
      padding-left: ${({ paddingX }) => paddingX || '1.5rem'};
      padding-right: ${({ paddingX }) => paddingX || '1.5rem'};
    }
    .dropdown-header {
      font-size: 0.9em;
    }
    .dropdown-item.disabled {
      color: var(--text-muted-color);
    }
  }
`;

const SelectedCheckmark = styled((props) => (
  <span {...props}>
    <CheckIcon className="mr-1" />
  </span>
))`
  width: 14px;
  margin-left: -14px; // position checkmark in left padding
  text-align: center;
  display: inline-block;
  svg {
    width: 10px;
    margin-bottom: 2px;
  }
`;

export const getOptionLabel = (option, labelKey) => {
  if (!option) return null;
  return typeof option === 'string' ? option : option[labelKey];
};

// Compare option labels to determine equality
export const defaultOptionsAreEqual = (option1, option2, { labelKey }) => {
  const optionLabel1 = getOptionLabel(option1, labelKey);
  const optionLabel2 = getOptionLabel(option2, labelKey);
  return optionLabel1 === optionLabel2;
};

export const optionsAreEqualById = (option1, option2) =>
  option1?.id === option2?.id;

export const StandardMenuItemChildren = ({
  newSelectionPrefix,
  option,
  optionsAreEqual = defaultOptionsAreEqual, // Determines if the two given options are equal
  selectedOption = null, // Currently selected option (single typeahead only)
  showCheckmark = true, // Render checkmark next to selected option (single typeahead only)
  showOverflow = false, // Wrap menu item text to next line or truncate with ellipsis
  showTitle = true, // Render title when hovering over option label
  state,
  children,
}) => {
  const { labelKey, text } = state;
  const optionLabel = getOptionLabel(option, labelKey);
  const selected =
    selectedOption && optionsAreEqual(option, selectedOption, state);
  const TextElement = showOverflow ? WrappedText : EllipsisText;
  return (
    <div className="d-flex align-items-center">
      {showCheckmark && selected && <SelectedCheckmark />}
      <div className="d-flex align-items-center justify-content-between w-100">
        <TextElement
          id={option.id || option[labelKey]}
          className={selected ? 'font-weight-bold' : ''}
          title={showTitle ? optionLabel : undefined}
        >
          {option.customOption && newSelectionPrefix}
          <Highlighter search={text}>{optionLabel}</Highlighter>
        </TextElement>
        {typeof children === 'function' ? children(option, state) : children}
      </div>
    </div>
  );
};

const StandardTypeahead = styled(
  // eslint-disable-next-line react/display-name
  forwardRef(
    (
      {
        clearTextOnFocus = false,
        closeMenuOnBlur = true,
        // Custom children to render inside the default menu item
        // Can be a function that accepts two parameters: (option, state)
        customMenuItemChildren,
        // Override the default render entirely
        renderMenuItemChildren,
        onChange,
        selected: propsSelected,
        ...props
      },
      forwardedRef,
    ) => {
      const ref = useRef();
      const typeaheadRef = forwardedRef ?? ref;
      const { selectedOption } = props;
      const initialSelected =
        propsSelected || (selectedOption ? [selectedOption] : []);
      const [selected, setSelected] = useState(initialSelected);
      const onFocus = () => {
        // Clear selected so that all options appear in dropdown
        setSelected([]);
      };
      const onBlur = () => {
        // Hide menu before setting selected
        if (closeMenuOnBlur) typeaheadRef.current?.hideMenu();
        // Reset selected if user has not made an explicit selection
        if (clearTextOnFocus && !selected.length) setSelected(initialSelected);
      };
      const onHideMenu = (value) => {
        // Hide menu before setting selected
        if (closeMenuOnBlur) typeaheadRef.current?.hideMenu();
        const [option] = value || [];
        setSelected(option ? [option] : []);
        typeaheadRef.current?.clear();
        typeaheadRef.current?.blur();
      };
      const onChangeSetSelected = (value) => {
        onChange(value);
        onHideMenu(value);
      };
      const onKeyDown = (e) => {
        if (e.code === 'Escape') onHideMenu();
      };

      const defaultRenderMenuItemChildren = (option, state) => (
        <StandardMenuItemChildren option={option} state={state} {...props}>
          {customMenuItemChildren}
        </StandardMenuItemChildren>
      );
      return (
        <Typeahead
          onBlur={onBlur}
          onChange={clearTextOnFocus ? onChangeSetSelected : onChange}
          onFocus={clearTextOnFocus ? onFocus : undefined}
          onKeyDown={clearTextOnFocus ? onKeyDown : undefined}
          ref={typeaheadRef}
          renderMenuItemChildren={
            renderMenuItemChildren || defaultRenderMenuItemChildren
          }
          selected={clearTextOnFocus ? selected : propsSelected}
          {...props}
        />
      );
    },
  ),
)`
  ${standardTypeaheadStyles}
`;

export default StandardTypeahead;

export const MarginlessTypeahead = (props) => (
  <StandardTypeahead paddingX="0" {...props} />
);
